import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { getMistakeAttempt } from '@/features/unified-learning/runtime/mistakeReviewService';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import type { KpActionRuntime, KpSubmitScenarioActionResponse } from '@/types/kursi-play-v2';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

export function RuntimeMistakeRetryScreen() {
  const { slug, uid, attemptId } = useLocalSearchParams<{ slug: string; uid: string; attemptId: string }>();
  const safeSlug     = typeof slug      === 'string' ? slug      : '';
  const safeUid      = typeof uid       === 'string' ? uid       : '';
  const safeAttemptId = typeof attemptId === 'string' ? attemptId : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<any | null>(null);
  const [lessonId, setLessonId] = useState<string>('');
  const [result, setResult] = useState<KpSubmitScenarioActionResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!safeSlug || !safeUid || !safeAttemptId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const detail = await getMistakeAttempt(safeAttemptId);
      if (cancelled) return;
      const runtime = await kpLiveApi.getLessonRuntime(detail.attempt.lesson_id, safeSlug, safeUid);
      if (cancelled) return;
      const sc = runtime.scenarios.find((x) => x.id === detail.attempt.scenario_id);
      if (!sc) throw new Error('התרחיש לא נמצא לניסיון חוזר');
      setScenario(sc);
      setLessonId(detail.attempt.lesson_id);
    } catch (e: unknown) {
      if (!cancelled) setError(e instanceof Error ? e.message : 'שגיאה בטעינת ניסיון חוזר');
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [safeSlug, safeUid, safeAttemptId]);

  useEffect(() => { void load(); }, [load]);

  const choose = useCallback(async (action: KpActionRuntime) => {
    if (!safeSlug || !safeUid || !scenario || !lessonId || submitting) return;
    setSubmitting(true);
    try {
      const res = await kpLiveApi.submitScenarioAction({
        scenario_id: scenario.id,
        action_id: action.id,
        lesson_id: lessonId,
        slug: safeSlug,
        user_id: safeUid,
      });
      setResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בשליחת פעולה');
    } finally {
      setSubmitting(false);
    }
  }, [safeSlug, safeUid, scenario, lessonId, submitting]);

  const qualityText = useMemo(() => {
    if (!result) return '';
    if (result.quality_tier === 'strong') return 'מעולה — שיפרת את ההחלטה!';
    if (result.quality_tier === 'acceptable') return 'סביר — שיפור חלקי, אפשר לדייק עוד.';
    return 'עדיין חלש/מסוכן — חזור לסקירת הטעויות.';
  }, [result]);

  if (loading) return <View style={s.center}><ActivityIndicator color={PRIMARY} size="large" /></View>;

  if (error || !scenario) {
    return (
      <View style={s.center}>
        <Text style={s.error}>{error ?? 'שגיאה בטעינה'}</Text>
        <Pressable style={s.primaryBtn} onPress={() => void load()}>
          <Text style={s.primaryBtnText}>נסה שוב</Text>
        </Pressable>
        <Pressable style={s.ghostBtn} onPress={() => router.back()}>
          <Text style={s.ghostBtnText}>חזרה</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Text style={s.title}>ניסיון חוזר</Text>
      <Text style={s.subtitle}>{scenario.title}</Text>
      <View style={s.card}>
        <Text style={s.body}>{scenario.situation}</Text>
        <Text style={s.body}>{scenario.decision_point}</Text>
      </View>

      {!result && scenario.actions.map((a: KpActionRuntime) => (
        <Pressable
          key={a.id}
          style={[s.actionBtn, submitting && { opacity: 0.55 }]}
          onPress={() => { void choose(a); }}
          disabled={submitting}
        >
          {submitting
            ? <ActivityIndicator color={PRIMARY} size="small" />
            : <Text style={s.actionText}>{a.label}</Text>
          }
        </Pressable>
      ))}

      {result && (
        <View style={s.card}>
          <Text style={s.resultTitle}>{qualityText}</Text>
          <Text style={s.body}>{result.consequence?.narrative ?? ''}</Text>
          {result.reinforcements.slice(0, 2).map((r, i) => (
            <Text key={i} style={s.body}>• {r.headline}: {r.body}</Text>
          ))}
          <Pressable style={s.primaryBtn} onPress={() => router.back()}>
            <Text style={s.primaryBtnText}>חזרה לפרטי הטעות</Text>
          </Pressable>
        </View>
      )}
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 10, maxWidth: 700, width: '100%', alignSelf: 'center' as any, paddingTop: 40 },
  center: { flex: 1, backgroundColor: BG, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 28, textAlign: 'right' },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  card: { backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 12, padding: 12, gap: 8 },
  body: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  actionBtn: { backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 10, padding: 11, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  actionText: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  resultTitle: { color: '#34D399', fontFamily: theme.fonts.bold, textAlign: 'right' },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 12, marginTop: 6 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold },
  ghostBtn: { borderRadius: 10, alignItems: 'center', paddingVertical: 10, borderWidth: 1, borderColor: BORDER },
  ghostBtnText: { color: TEXT_MED, fontFamily: theme.fonts.regular },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
});
